import { NavLink, Outlet } from 'react-router-dom';
import { useLazyLoadQuery } from 'react-relay';

import { MeQuery } from '../components/guards';
import { t } from '../i18n/t';
import { registerFor } from '../profile/register';

import type { guardsMeQuery } from '../__generated__/guardsMeQuery.graphql';

const tab = ({ isActive }: { isActive: boolean }) => `tab flex-1 min-h-(--hit-min) ${isActive ? 'tab-active' : ''}`;

/**
 * The progress screen's shell: two tabs for an adult, none for a child.
 *
 * One screen was a count, the phrases, three charts, twenty practice cards and
 * twenty conversations, all under one query. The conversations are the part
 * with a different job -- reading back one debrief rather than seeing a trend
 * -- so they are the part that moved, and the charts' twelve-week window no
 * longer loads to show a list.
 *
 * A child gets no tabs, because the second tab is debriefs and a child is
 * shown none (spec §5). `ProgressConversations` redirects a typed URL.
 */
export function ProgressLayout() {
  // The same `me` the route guards already read, so this is a store hit.
  const data = useLazyLoadQuery<guardsMeQuery>(MeQuery, {});
  if (registerFor(data.me?.learner?.defaultCharacter?.audience) === 'kid') return <Outlet />;

  return (
    <div className="flex flex-col gap-6 py-6">
      <h1 className="text-2xl font-bold">{t('progress-title')}</h1>
      {/* Links, not a `tablist`: the same reasoning as `AccountView`'s. */}
      <nav className="tabs tabs-box">
        {/* `end`, or `/history` also matches `/history/conversations` and both tabs light. */}
        <NavLink to="" end className={tab}>
          {t('progress-tab-overview')}
        </NavLink>
        <NavLink to="conversations" className={tab}>
          {t('progress-tab-conversations')}
        </NavLink>
      </nav>
      <Outlet />
    </div>
  );
}
